import React from 'react';

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>
        
        <div className="prose prose-slate max-w-none">
          <p className="mb-6">
            IMPERIAL LABS LTD ("we," "us," or "our") operates this CV generation service (the "Service"). 
            This Privacy Policy explains how we collect, use, disclose, and protect information that identifies 
            or could be used to identify you ("Personal Data"). By accessing or using our Service, you acknowledge 
            that you have read and understood this Privacy Policy. If you do not agree with our policies and 
            practices, please do not use our Service.
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">1. WHO WE ARE</h2>
            <p>Company Name: IMPERIAL LABS LTD</p>
            <p>Contact Email: Team@Blueswissgroup.com</p>
            <p>
              We process your Personal Data in compliance with the UK General Data Protection Regulation 
              (UK GDPR) and other applicable laws in the United Kingdom.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">2. DATA WE COLLECT</h2>
            <h3 className="text-lg font-medium mb-2">2.1. Personal Data You Provide Directly</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Account Registration Information: Name, email address, and password.</li>
              <li>Resume or Profile Data: Employment history, education, skills, references, or any other information you include in your CV or resume.</li>
              <li>Payment Information (if applicable): For premium features, we may collect billing addresses, payment card details, and transaction information.</li>
              <li>Communications: Any correspondence you send us, including customer support requests or feedback.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">2.2. Data Collected Automatically</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Technical Information: IP address, browser type and version, operating system, device identifiers, and system configuration.</li>
              <li>Usage Data: Pages viewed, links clicked, time spent on the Service, and the date and time of any requests.</li>
              <li>Location Information (if enabled): Approximate geographic location derived from IP address or device settings.</li>
            </ul>
          </section>


          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">FULL PRIVACY POLICY</h2>
            <p>
            # PRIVACY POLICY

**Last Updated: [Date]**

IMPERIAL LABS LTD (“we,” “us,” or “our”) operates [cv-master-ai.com/CV Tailor Pro] (the “Service”). This Privacy Policy explains how we collect, use, disclose, and protect information that identifies or could be used to identify you (“Personal Data”). By accessing or using our Service, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with our policies and practices, please do not use our Service.

---

## 1. WHO WE ARE

- **Company Name:** IMPERIAL LABS LTD
- **Registered Office Address:** 140 Brunswick Road, Sheffield, S3 9LR, UK
- **Contact Email:** Team@blueswissgroup.com

We process your Personal Data in compliance with the UK General Data Protection Regulation (UK GDPR) and other applicable laws in the United Kingdom.

---

## 2. DATA WE COLLECT

### 2.1. Personal Data You Provide Directly

We collect information that you voluntarily provide when you use our Service, such as:

- **Account Registration Information:** Name, email address, and password.
- **Resume or Profile Data:** Employment history, education, skills, references, or any other information you include in your CV or resume.
- **Payment Information (if applicable):** For premium features, we may collect billing addresses, payment card details, and transaction information.
- **Communications:** Any correspondence you send us, including customer support requests or feedback.

### 2.2. Data Collected Automatically

When you use or access our Service, certain data is collected automatically, such as:

- **Technical Information:** IP address, browser type and version, operating system, device identifiers, and system configuration.
- **Usage Data:** Pages viewed, links clicked, time spent on the Service, and the date and time of any requests.
- **Location Information (if enabled):** Approximate geographic location derived from IP address or device settings.

### 2.3. Data from Third Parties

We may receive additional information about you from third parties, such as:

- **Social Media or LinkedIn Integration:** If you choose to log in or connect through a social account.
- **Job Boards/Partner Sites:** Data shared if you apply to job listings or use partner integrations.

---

## 3. HOW WE USE YOUR DATA

We use your Personal Data to:

1. **Provide Our Services:** Enable you to create or update your resume, autofill job application fields, and track job applications.
2. **Personalize Your Experience:** Tailor features, recommendations, and content to your preferences.
3. **Process Transactions (if applicable):** Manage payments and subscriptions, and provide invoices or transaction confirmations.
4. **Communicate with You:** Send you updates, security alerts, support messages, and account notifications.
5. **Maintain & Improve Our Service:** Monitor, analyze, and enhance the user experience, troubleshoot technical issues, and improve platform functionality.
6. **Ensure Compliance and Protect Our Rights:** Enforce our Terms of Service, comply with legal obligations, and defend against legal claims.

---

## 4. LEGAL BASES FOR PROCESSING

We process Personal Data under the following legal bases:

- **Consent:** When you have given clear consent to process your data for a specific purpose.
- **Contractual Necessity:** When processing is necessary to perform or conclude a contract with you (e.g., providing our Services).
- **Legal Obligation:** When we need to comply with a legal or regulatory obligation.
- **Legitimate Interests:** When it is in our legitimate business interests and does not override your data protection rights (e.g., improving our platform).

---

## 5. COOKIES AND SIMILAR TECHNOLOGIES

We use cookies and similar tracking technologies to:

- **Authenticate Users:** Remember your login details to keep you signed in.
- **Analyze Usage:** See how users engage with our Service to improve features and functionality.
- **Personalize Content & Ads (if applicable):** Deliver relevant content based on your interests.

You can control cookies through your browser settings, though disabling certain cookies may limit your use of some features on our Service.

---

## 6. DATA SHARING AND DISCLOSURES

We may share your Personal Data with:

- **Service Providers:** Trusted third parties that help us operate our Service (e.g., hosting providers, payment processors, analytics services).
- **Business Partners:** When you integrate your account with third-party job boards or professional networks, with your permission.
- **Legal Authorities:** If required by law or in good-faith belief that disclosure is necessary to comply with legal obligations or protect rights and safety.
- **Corporate Transactions:** In the event of a merger, acquisition, or sale of assets, Personal Data may be transferred to the acquiring entity.

---

## 7. INTERNATIONAL DATA TRANSFERS

Although we are based in the United Kingdom, some of our service providers or partners may be located in countries that do not provide the same level of data protection as the UK. Where required, we implement safeguards such as Standard Contractual Clauses or rely on adequacy decisions to ensure your Personal Data is protected.

---

## 8. DATA SECURITY

We take reasonable administrative, technical, and physical measures to protect your Personal Data. Despite our efforts, no method of transmission over the internet or method of electronic storage is completely secure. We encourage you to use strong passwords and keep your login details confidential.

---

## 9. DATA RETENTION

We retain Personal Data for as long as is necessary to provide our Services or comply with legal obligations. When your Personal Data is no longer required, we securely erase or anonymize it.

---

## 10. YOUR RIGHTS

Under the UK GDPR, you have the following rights (subject to certain conditions and exceptions):

- **Access:** Request a copy of your Personal Data.
- **Rectification:** Ask us to correct or update inaccurate or incomplete data.
- **Erasure:** Request deletion of your data when it is no longer needed.
- **Restriction of Processing:** Limit how we process your data.
- **Data Portability:** Request a copy of your Personal Data in a structured, commonly used format.
- **Objection:** Object to the processing of your data in certain circumstances.
- **Withdraw Consent:** If processing is based on consent, you may withdraw it at any time.

To exercise any of these rights, please contact us at [Team@BlueSwissGroup.com](mailto:Team@BlueSwissGroup.com) . If you believe we have infringed upon your rights, you also have the right to lodge a complaint with the UK Information Commissioner’s Office (ICO).

---

## 11. THIRD-PARTY LINKS

Our Service may contain links to third-party websites or services. This Privacy Policy does not apply to those external sites, and we are not responsible for their practices. We encourage you to review the privacy policies of any third-party services you visit.

---

## 12. CHILDREN’S PRIVACY

Our Service is not intended for children under the age of 16. We do not knowingly collect personal data from children under 16. If you believe a child has provided Personal Data to us, please contact us at **[Insert Contact Email]**, and we will take steps to delete such data.

---

## 13. CHANGES TO THIS PRIVACY POLICY

We may update this Privacy Policy from time to time. When we do, we will revise the “Last Updated” date at the top of the policy. We encourage you to review this page periodically to stay informed about how we protect your Personal Data.

---

## 14. CONTACT US

If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices,
            </p>
            <p className="mt-4">
              IMPERIAL LABS LTD<br />
              Email: <a href="mailto:Team@Blueswissgroup.com" className="text-emerald-600 hover:text-emerald-700">
                Team@Blueswissgroup.com
              </a>
            </p>
          </section>
        </div>

        <footer className="mt-8 pt-6 border-t text-sm text-gray-500">
          <p>© {new Date().getFullYear()} IMPERIAL LABS LTD. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
} 